import { Box, Button, Divider, Flex, Input, Text } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useAuth } from "../../../Hooks/useAuth";
import { api } from "../../../services/api";
import { EditSubcategory } from "../EditSubcategory";
import { FiPlus, FiX } from "react-icons/fi";
import swal from "sweetalert";

const GREEN  = "#004a33";
const ORANGE = "#fc8f04";

export function SubCategoryCard({ allSubcategories, allCategories, allProductTypes, allProducts }) {
  const { user } = useAuth();
  const [subcategoryName, setSubcategoryName] = useState("");
  const [subcategories, setSubcategories] = useState(allSubcategories);
  const [showForm, setShowForm] = useState(false);

  // Related-data lookups for the "quantidade de produtos" shown per row —
  // built once from the dashboard's already-fetched data instead of a
  // dedicated endpoint per subcategory.
  const categoriesBySubcategory = useMemo(() => {
    const map = {};
    (allCategories || []).forEach(c => {
      (map[c.subcategory_id] ||= []).push(c);
    });
    return map;
  }, [allCategories]);
  const productTypesByCategory = useMemo(() => {
    const map = {};
    (allProductTypes || []).forEach(pt => {
      (map[pt.category_id] ||= []).push(pt);
    });
    return map;
  }, [allProductTypes]);
  const productCountByType = useMemo(() => {
    const map = {};
    (allProducts || []).forEach(p => {
      map[p.product_type_id] = (map[p.product_type_id] || 0) + 1;
    });
    return map;
  }, [allProducts]);

  function relatedInfo(subcategory) {
    const cats = categoriesBySubcategory[subcategory.id] || [];
    const types = cats.flatMap(c => productTypesByCategory[c.id] || []);
    const productCount = types.reduce((sum, t) => sum + (productCountByType[t.id] || 0), 0);
    return { categoryCount: cats.length, categoryNames: cats.map(c => c.name), typeCount: types.length, productCount };
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!subcategoryName.trim()) {
      swal({ title: "Atenção", text: "Nome não pode ficar em branco.", icon: "info" });
      return;
    }
    const response = await api.post("api/v1/subcategories", { name: subcategoryName, user_id: user.id }, {
      headers: { "access-token": user.token, client: user.client, uid: user.uid, accept: "application/json" },
    });
    if (response.status === 200) {
      setSubcategories(prev => [...prev, response.data]);
      setSubcategoryName("");
      setShowForm(false);
      swal({ title: "Criado!", text: "Subcategoria criada com sucesso.", icon: "success" });
    } else {
      swal({ title: "Erro", text: "Algo deu errado.", icon: "error" });
    }
  }

  const updateSubcategoryName = (subcategoryId, newName) => {
    setSubcategories(prev => prev.map(s => s.id === subcategoryId ? { ...s, name: newName } : s));
  };

  return (
    <Box>
      {/* Section header */}
      <Flex align="center" justify="space-between" mb="20px">
        <Box>
          <Text fontSize="11px" fontWeight="800" color="gray.400" textTransform="uppercase" letterSpacing="0.08em">
            Catálogo
          </Text>
          <Text fontSize="20px" fontWeight="900" color={GREEN}>
            Subcategorias
            <Text as="span" fontSize="14px" fontWeight="600" color="gray.400" ml="8px">
              ({subcategories.length})
            </Text>
          </Text>
        </Box>
        <Button
          size="sm" borderRadius="8px" px="14px"
          bg={showForm ? "gray.100" : GREEN} color={showForm ? "gray.600" : ORANGE}
          fontWeight="700" fontSize="12px" _hover={{ opacity: 0.88 }}
          leftIcon={showForm ? <FiX size={13} /> : <FiPlus size={13} />}
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? "Cancelar" : "Nova subcategoria"}
        </Button>
      </Flex>

      {/* Create form */}
      {showForm && (
        <Box
          bg="white" borderRadius="12px" p="16px 20px" mb="16px"
          border="1px solid rgba(0,74,51,.12)" boxShadow="0 2px 8px rgba(0,74,51,.06)"
        >
          <Text fontSize="12px" fontWeight="700" color="gray.500" mb="10px">Nova subcategoria</Text>
          <Flex gap="10px">
            <Input
              flex="1"
              placeholder="Nome da subcategoria"
              value={subcategoryName}
              onChange={e => setSubcategoryName(e.target.value)}
              borderRadius="8px"
              fontSize="14px"
              border="1px solid rgba(0,74,51,.2)"
              _focus={{ borderColor: GREEN, boxShadow: `0 0 0 1px ${GREEN}` }}
              onKeyDown={e => e.key === "Enter" && handleCreate(e)}
            />
            <Button
              borderRadius="8px" px="20px"
              bg={GREEN} color={ORANGE} fontWeight="700" fontSize="13px"
              _hover={{ opacity: 0.88 }}
              onClick={handleCreate}
            >
              Cadastrar
            </Button>
          </Flex>
        </Box>
      )}

      {/* List */}
      <Box bg="white" borderRadius="14px" border="1px solid rgba(0,74,51,.08)" overflow="hidden" boxShadow="0 2px 10px rgba(0,74,51,.04)">
        {subcategories.length === 0 ? (
          <Box py="32px" textAlign="center">
            <Text fontSize="14px" color="gray.400">Nenhuma subcategoria cadastrada</Text>
          </Box>
        ) : (
          subcategories.map((sub, i) => {
            const { categoryCount, categoryNames, typeCount, productCount } = relatedInfo(sub);
            return (
            <Box key={sub.id}>
              <Flex
                align="center" justify="space-between"
                px="20px" py="12px"
                _hover={{ bg: "rgba(0,74,51,.025)" }}
                transition="background 0.1s"
              >
                <Flex align="center" gap="10px" flex="1" minW="0">
                  <Text fontSize="11px" fontWeight="700" color="gray.300" minW="28px">#{sub.id}</Text>
                  <Box minW="0">
                    <Text fontSize="14px" fontWeight="600" color="gray.700">{sub.name}</Text>
                    <Text fontSize="11px" color="gray.400" mt="2px" noOfLines={1}>
                      {categoryCount} categoria{categoryCount !== 1 ? "s" : ""} · {typeCount} tipo{typeCount !== 1 ? "s" : ""} de produto · {productCount} produto{productCount !== 1 ? "s" : ""}
                      {categoryNames.length > 0 && ` — ${categoryNames.join(", ")}`}
                    </Text>
                  </Box>
                </Flex>
                <EditSubcategory subcategoryId={sub.id} onUpdateSubcategory={updateSubcategoryName} />
              </Flex>
              {i < subcategories.length - 1 && <Divider borderColor="rgba(0,74,51,.06)" />}
            </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}
